import Phaser from 'phaser';
import {ACTIVE_GLOW, UNIT_GLOW_TEXTURE} from '../../configs/activeGlow';
import {computeGlowPixels} from './computeGlowPixels';
import {cropPixels, findContentBounds} from './trimTextureFrame';

/**
 * Текстура мягкого свечения по силуэту существующей текстуры (активные отряды, см.
 * scenes/ActiveUnitGlows). Считается при первом показе этого типа отряда и кэшируется
 * по ключу. Как и обводка, живет в массиве, а не в canvas: так она переживает потерю
 * GPU-контекста (см. generateOutlineTexture).
 *
 * Спрайту нужен масштаб иконки, деленный на sourceScale: картинка уменьшена.
 *
 * @param {Phaser.Scene} scene
 * @param {string} sourceKey — ключ исходной текстуры
 * @param {string} glowKey — ключ текстуры свечения
 * @returns {string|null} glowKey или null, если источник пока не прочитался
 */
export function generateGlowTexture(scene, sourceKey, glowKey) {
  const textureManager = scene.textures;
  if (textureManager?.exists?.(glowKey)) {
    return glowKey;
  }

  const sourceImage = textureManager?.get?.(sourceKey)?.getSourceImage?.();
  const srcWidth = sourceImage ? (sourceImage.naturalWidth || sourceImage.width) : 0;
  const srcHeight = sourceImage ? (sourceImage.naturalHeight || sourceImage.height) : 0;
  if (!srcWidth || !srcHeight || typeof document === 'undefined') {
    return null;
  }

  const {sourceScale} = UNIT_GLOW_TEXTURE;
  const width = Math.max(1, Math.round(srcWidth * sourceScale));
  const height = Math.max(1, Math.round(srcHeight * sourceScale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', {willReadFrequently: true});
  if (!context) {
    return null;
  }
  context.imageSmoothingEnabled = true;
  context.drawImage(sourceImage, 0, 0, width, height);

  const glow = computeGlowPixels(context.getImageData(0, 0, width, height).data, width, height, {
    ...UNIT_GLOW_TEXTURE,
    innerColor: ACTIVE_GLOW.glowInner,
    outerColor: ACTIVE_GLOW.glowOuter,
  });
  if (!glow) {
    return null;
  }
  // Как и у обводки: в текстуре только само свечение (см. generateOutlineTexture)
  const bounds = findContentBounds(glow.data, glow.width, glow.height);
  const texture = textureManager.addUint8Array(
    glowKey, cropPixels(glow.data, glow.width, bounds), bounds.width, bounds.height,
  );
  if (!texture) {
    return null;
  }
  texture.get?.().setTrim(glow.width, glow.height, bounds.x, bounds.y, bounds.width, bounds.height);
  texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
  return glowKey;
}
